import React, { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ConfigContext } from "../context/GlobalContext";
import { Icon } from "@iconify/react/dist/iconify.js";
import * as actionType from '../context/actions';
import { Languages } from "../Utils/Languages";
import Currancy from "../Utils/currency.json";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import InvoiceTableDataLayer from "./InvoiceTableDataLayer";

const CustomerView = () => {
  const { state, dispatch } = useContext(ConfigContext);
  const navigate = useNavigate();

  const customer_data = state?.SELECTEDCUSTOMER;
  const selectedCustomerId = String(customer_data?.customerId ?? "");

  const filterCurrency = state?.CUSTOMER_CURRENCY_FILTER || "All";
  const displayCurrency = filterCurrency !== "All" ? filterCurrency : (customer_data?.currency || state?.CURRENCYCODE || "INR");

  const matchedCurrency = displayCurrency ? Currancy.CountryCurrencyResponse.find(
    (c) => c.currencyCode === displayCurrency
  ) : null;
  const currencySymbol = matchedCurrency ? matchedCurrency.currencytSymbol : state?.SYMBOL;
  const currencyCode = matchedCurrency ? matchedCurrency.currencyCode : state?.CURRENCYCODE;

  // Get customer invoices by matching customerId instead of using customer_data.invoices array
  const customerInvoices = (state?.INVOICES || []).filter(
    (inv) => {
      const matchCustomer = inv.customerId && String(inv.customerId) === selectedCustomerId;
      if (!matchCustomer) return false;
      if (filterCurrency === "All") return true;
      const invCurrency = inv.currency || state?.CURRENCYCODE || "INR";
      return invCurrency === filterCurrency;
    }
  );

  // Calculate totals
  const totalAmount = customerInvoices.reduce((sum, inv) => sum + (Number(inv?.totalAmount) || 0), 0);

  // Calculate invoice counts
  const totalInvoicesCount = customerInvoices.length;
  const paidInvoicesCount = customerInvoices.filter(inv => inv.paidFlagKey === "true" || inv.paidFlagKey === true).length;
  const outstandingInvoicesCount = totalInvoicesCount - paidInvoicesCount;

  // Ensure paymentsReceived is an array
  const payments = (Array.isArray(state?.SELECTEDCUSTOMER?.paymentsReceived)
    ? state.SELECTEDCUSTOMER.paymentsReceived
    : []
  ).filter(p => {
    if (filterCurrency === "All") return true;
    const paymentCurrency = p.currency || customer_data?.currency || state?.CURRENCYCODE || "INR";
    return paymentCurrency === filterCurrency;
  });

  // Sum up all paid amounts
  const totalPaidAmount = payments.reduce(
    (sum, payment) => sum + (Number(payment?.amount) || 0),
    0
  );
  const outstandingAmount = totalAmount - totalPaidAmount;

  const availableCurrencies = React.useMemo(() => {
    const list = new Set(["INR", "USD"]);
    (state?.INVOICES || []).forEach(inv => {
      if (inv.currency) list.add(inv.currency);
    });
    return Array.from(list);
  }, [state?.INVOICES]);


  const formatCurrency = (value) =>
    `${currencySymbol === "$" ? currencyCode + " " + currencySymbol : currencySymbol} ${value}`;

  const onBack = () => {
    navigate(-1);
  };

  // Prepare financial data for charts
  const financialData = (() => {
    const monthlyMap = {};

    customerInvoices.forEach(inv => {
      const date = new Date(Number(inv.date));
      const year = date.getFullYear();
      const monthName = date.toLocaleString('default', { month: 'short' });
      const key = `${monthName} ${year}`;

      if (!monthlyMap[key]) monthlyMap[key] = { month: key, amount: 0, paid: 0 };

      monthlyMap[key].amount += Number(inv.totalAmount || 0);
    });

    const chartData = Object.values(monthlyMap).sort((a, b) => new Date(a.month) - new Date(b.month));

    // cumulative paid distribution
    let cumulativePaid = 0;
    const overallTotal = chartData.reduce((sum, m) => sum + m.amount, 0);
    chartData.forEach((data) => {
      const monthPaid = (data.amount / overallTotal) * totalPaidAmount;
      cumulativePaid += monthPaid;
      data.paid = cumulativePaid;
    });

    // Pie chart
    const totalInvoiceAmount = customerInvoices.reduce((sum, inv) => sum + Number(inv.totalAmount || 0), 0);
    const pieData = [
      { name: 'Paid', value: totalPaidAmount, color: '#10B981' },
      { name: 'Outstanding', value: totalInvoiceAmount - totalPaidAmount, color: '#F59E0B' },
    ];

    return { chartData, pieData };
  })();


  // const financialData = (() => {
  //   // Dummy data for 12 months
  //   const months = [
  //     "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  //     "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  //   ];

  //   let chartData = months.map((month, idx) => {
  //     const amount = Math.floor(Math.random() * 1000000) + 50000; // 50k → 1M
  //     const paid = Math.floor(amount * (Math.random() * 0.9 + 0.1)); // 10% → 100% of amount
  //     return { month: `${month} 2025`, amount, paid };
  //   });

  //   // Pie chart
  //   const totalInvoiceAmount = chartData.reduce((sum, m) => sum + m.amount, 0);
  //   const totalPaidAmount = chartData.reduce((sum, m) => sum + m.paid, 0);
  //   const pieData = [
  //     { name: "Paid", value: totalPaidAmount, color: "#10B981" },
  //     { name: "Outstanding", value: totalInvoiceAmount - totalPaidAmount, color: "#F59E0B" },
  //   ];

  //   return { chartData, pieData };
  // })();

  // Prepare a list of invoice objects for this customer
  const customerInvoiceList = customerInvoices;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-primary-50 p-6 rounded-xl border border-gray-300 shadow-md text-sm">
          <p className="mb-2 font-semibold">{label}</p>
          {payload.map((entry, index) => (
            <p key={`value-${index}`} className="m-0 mb-1">
              <span className="font-medium" style={{ color: entry.color }}>
                {entry.name}:
              </span>{' '}
              {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }

    return null;
  };

  const formatYAxis = (value) => {
    let formattedValue = value;

    if (value >= 10000000) {
      formattedValue = (value / 10000000).toFixed(2) + " Cr"; // Crores
    } else if (value >= 100000) {
      formattedValue = (value / 100000).toFixed(2) + " L"; // Lakhs
    } else if (value >= 1000) {
      formattedValue = (value / 1000).toFixed(1) + "k"; // Thousands
    }

    return ` ${formattedValue}`;
  };

  return (
    <div className="card shadow-lg p-4 w-80 mt-20 position-relative">
      <div className="card-header">

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          {/* Left Side - Back/Close Button */}
          <div className="d-flex align-items-center gap-2">
            <button
              onClick={() => { navigate(-1) }}
            >
              <i className="fa fa-arrow-left" style={{ fontSize: 18 }}></i>
            </button>
            <p className='fw-semibold mb-0' style={{ fontSize: 20 }}>{state?.SELECTEDCUSTOMER?.customer}</p>
          </div>

          {/* Currency Filter Dropdown */}
          <div className="dropdown">
            <button
              className="btn btn-outline-primary-600 dropdown-toggle px-16 py-8 radius-8 d-flex align-items-center gap-2"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              style={{ minWidth: "160px" }}
            >
              <Icon icon="lucide:coins" className="text-lg" />
              <span>
                {state?.CUSTOMER_CURRENCY_FILTER || state?.CURRENCYCODE || "INR"}
              </span>
            </button>
            <ul className="dropdown-menu shadow-md border-0 radius-8 p-8" style={{ zIndex: 1050 }}>
              {availableCurrencies.map(curr => (
                <li key={curr} className="mt-1">
                  <button
                    className={`dropdown-item px-16 py-8 rounded ${state?.CUSTOMER_CURRENCY_FILTER === curr ? "bg-primary text-white" : "text-secondary-light bg-hover-neutral-200"}`}
                    onClick={() => dispatch({ type: actionType.CUSTOMER_CURRENCY_FILTER, payload: curr })}
                  >
                    {curr}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

      </div>
      <div className="card-body">
        <div className="row">
          {/* Total Amount */}
          <div className="col-xxl-4 col-sm-4">
            <div className="card px-24 py-16 shadow-none radius-8 border h-100 bg-gradient-start-3">
              <div className="card-body p-0">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                  <div className="d-flex align-items-center">
                    <div className="w-64-px h-64-px radius-16 bg-base-50 d-flex justify-content-center align-items-center me-20">
                      <span className="mb-0 w-40-px h-40-px bg-primary-600 flex-shrink-0 text-white d-flex justify-content-center align-items-center radius-8 h6 mb-0">
                        <Icon icon="fa6-solid:sack-dollar" className="icon" />
                      </span>
                    </div>
                    <div>
                      <span className="mb-2 fw-medium text-secondary-light text-md">
                        Total Amount
                      </span>
                      <h6 className="fw-semibold my-1">{currencySymbol === "$" ? `${currencyCode} ${currencySymbol} ` : `${currencySymbol} `} {totalAmount.toFixed(2)}</h6>
                      <p className="text-sm mb-0">Total Invoices: {totalInvoicesCount}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Paid Amount */}
          <div className="col-xxl-4 col-sm-4">
            <div className="card shadow-none border bg-gradient-start-4 px-24 py-16 radius-8">
              <div className="card-body p-0">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                  <div className="d-flex align-items-center">
                    <div className="w-64-px h-64-px radius-16 bg-base-50 d-flex justify-content-center align-items-center me-20">
                      <span className="mb-0 w-40-px h-40-px bg-success flex-shrink-0 text-white d-flex justify-content-center align-items-center radius-8 h6 mb-0">
                        <Icon icon="fa6-solid:circle-check" className="text-white text-2xl mb-0" />
                      </span>
                    </div>
                    <div>
                      <span className="mb-2 fw-medium text-secondary-light text-md">
                        Paid Amount
                      </span>
                      <h6 className="fw-semibold my-1">{currencySymbol === "$" ? `${currencyCode} ${currencySymbol} ` : `${currencySymbol} `} {totalPaidAmount}</h6>
                      <p className="text-sm mb-0">Paid Invoices: {paidInvoicesCount}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Outstanding Amount */}
          <div className="col-xxl-4 col-sm-4">
            <div className="card px-24 py-16 shadow-none radius-8 border h-100 bg-gradient-start-5">
              <div className="card-body p-0">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                  <div className="d-flex align-items-center">
                    <div className="w-64-px h-64-px radius-16 bg-base-50 d-flex justify-content-center align-items-center me-20">
                      <span className="mb-0 w-40-px h-40-px bg-red flex-shrink-0 text-white d-flex justify-content-center align-items-center radius-8 h6 mb-0">
                        <Icon icon="fa6-solid:circle-exclamation" className="text-white text-2xl mb-0" />
                      </span>
                    </div>
                    <div>
                      <span className="mb-2 fw-medium text-secondary-light text-md">
                        Outstanding Amount
                      </span>
                      <h6 className="fw-semibold my-1">{currencySymbol === "$" ? `${currencyCode} ${currencySymbol} ` : `${currencySymbol} `} {outstandingAmount.toFixed(2)}</h6>
                      <p className="text-sm mb-0">Unpaid Invoices: {outstandingInvoicesCount}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {financialData.chartData.length > 0 && (
            <div className="row mb-4">
              <div className="col-12 col-lg-12 mt-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-transparent">
                    <h6 className="mb-0 fw-semibold">Monthly Revenue Trend</h6>
                  </div>
                  <div className="card-body">
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={financialData.chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={formatYAxis} width={80} />
                        <Tooltip content={<CustomTooltip />} />
                        <Line
                          type="monotone"
                          dataKey="amount"
                          stroke="#3B82F6"
                          strokeWidth={3}
                          name="Total Amount"
                          dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="paid"
                          stroke="#10B981"
                          strokeWidth={3}
                          name="Paid Amount"
                          dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* <div className="col-12 col-lg-4 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-transparent">
                    <h6 className="mb-0 fw-semibold">Payment Status</h6>
                  </div>
                  <div className="card-body d-flex align-items-center justify-content-center">
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={financialData.pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {financialData.pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [formatCurrency(value), '']} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="card-footer bg-transparent">
                    <div className="d-flex justify-content-center gap-3">
                      <div className="d-flex align-items-center">
                        <div className="badge bg-success me-2"></div>
                        <small>Paid</small>
                      </div>
                      <div className="d-flex align-items-center">
                        <div className="badge bg-warning me-2"></div>
                        <small>Outstanding</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div> */}
            </div>
          )}

          {/* <div className="col-12 col-lg-6 d-flex gap-2 mb-3">
            <span className="fw-medium text-primary-light">{Languages[state?.LANGUAGE].name}</span>
            <span className="text-secondary-light fw-regular">: {customer_data.customer}</span>
          </div>
          <div className="col-12 col-lg-6 d-flex gap-2 mb-3">
            <span className="fw-medium text-primary-light">{Languages[state?.LANGUAGE].phone}</span>
            <span className="text-secondary-light fw-regular">: {customer_data.dialCode}{customer_data.phone}</span>
          </div>


          <div className="col-12 col-lg-6 d-flex gap-2 mb-3">
            <span className="fw-medium text-primary-light">{Languages[state?.LANGUAGE].email}</span>
            <span className="text-secondary-light fw-regular">: {customer_data.email}</span>
          </div>
          <div className="col-12 col-lg-6 d-flex gap-2 mb-3">
            <span className="fw-medium text-primary-light">{(customer_data?.currency === "AUD" || state?.CURRENCYCODE === "AUD" || state?.TAXTYPE === "ABN")
              ? "ABN Number"
              : state?.TAXTYPE === "GST"
                ? `${Languages[state?.LANGUAGE]?.gst} Number`
                : `${Languages[state?.LANGUAGE]?.tax} Number`}</span>
            <span className="text-secondary-light fw-regular">: {customer_data.customerGSTNo}</span>
          </div>


          <div className="col-12 d-flex gap-2 mb-3">
            <span className="fw-medium text-primary-light">{Languages[state?.LANGUAGE].address}</span>
            <span className="text-secondary-light fw-regular">: {customer_data.address}</span>
          </div> */}

          <div className="col-12">
            <div className="card-header bg-transparent">
              <h6 className="mb-0 fw-semibold">Releted Invoices</h6>
            </div>
            <InvoiceTableDataLayer InvoicesData={customerInvoiceList} selectedBusiness={state?.SELECTEDBUSINESS} />
          </div>
        </div>

      </div>
    </div>
  );
};

export default CustomerView;
